import { FC } from 'react';
import type { RequirementGroup } from '@teachur/http-client';
import {
  ProgressSegment,
  SegmentedProgressBar,
} from '@new/common/ui/SegmentedProgressBar/SegmentedProgressBar';
import { sumCredits } from '../../lib/planView';

interface DegreePlanProgressBarProps {
  groups: RequirementGroup[];
  onSelectGroup?: (groupId: string) => void;
}

const TRANSFERRED_COLOR = 'bg-(--secondary-B900)';
const NEWLANE_COLOR = 'bg-(--primary-O900)';
const REMAINING_COLOR = 'bg-(--neutral-black-NB200)';

export const DegreePlanProgressBar: FC<DegreePlanProgressBarProps> = ({
  groups,
  onSelectGroup,
}) => {
  const creditsRequired = groups.reduce((total, group) => total + group.requiredCredits, 0);

  if (creditsRequired <= 0) {
    return null;
  }

  const transferredSegments: ProgressSegment[] = [];
  const newlaneSegments: ProgressSegment[] = [];

  groups.forEach((group) => {
    if (group.courses.length === 0) {
      return;
    }
    const transferredCredits = sumCredits(
      group.courses.filter((course) => course.source === 'transfer')
    );
    const newlaneCredits = sumCredits(
      group.courses.filter((course) => course.source !== 'transfer')
    );
    if (transferredCredits > 0) {
      transferredSegments.push({
        id: `${group.id}-transferred`,
        groupId: group.id,
        credits: transferredCredits,
        color: TRANSFERRED_COLOR,
        title: `${group.title} ${transferredCredits}/${group.requiredCredits}`,
      });
    }
    if (newlaneCredits > 0) {
      newlaneSegments.push({
        id: `${group.id}-newlane`,
        groupId: group.id,
        credits: newlaneCredits,
        color: NEWLANE_COLOR,
        title: `${group.title} ${newlaneCredits}/${group.requiredCredits}`,
      });
    }
  });

  const creditsSelected = sumCredits(groups.flatMap((group) => group.courses));
  const creditsRemaining = Math.max(creditsRequired - creditsSelected, 0);

  const segments: ProgressSegment[] = [
    ...transferredSegments,
    ...newlaneSegments,
    ...(creditsRemaining > 0
      ? [
          {
            id: 'remaining',
            groupId: null,
            credits: creditsRemaining,
            color: REMAINING_COLOR,
            title: `Credits to choose ${creditsRemaining}`,
          },
        ]
      : []),
  ];

  return (
    <SegmentedProgressBar
      segments={segments}
      total={creditsRequired}
      legend={[
        { label: 'Transferred', color: TRANSFERRED_COLOR },
        { label: 'Take on Newlane', color: NEWLANE_COLOR },
      ]}
      hint="Hover over a section to see its category"
      onSelectGroup={onSelectGroup}
    />
  );
};
